import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { BookOpen, Languages, GraduationCap, ArrowRight, Lock, ShieldCheck, Star, Layers, BookMarked, Scale, Brain, Sparkles, Headphones, AlertTriangle } from 'lucide-react';

const TajweedGuideSection = () => {
  const { language } = useLanguage();
  const sv = language === 'sv';

  return (
    <section className="py-24 relative overflow-hidden" data-testid="tajweed-guide-section">
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(206,150,8,0.03) 0%, transparent 50%, rgba(11,61,46,0.03) 100%)' }} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* Header with hadith quote */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-6 tracking-wider"
            style={{ backgroundColor: 'rgba(206,150,8,0.1)', color: 'var(--gold)', border: '1px solid rgba(206,150,8,0.2)' }}>
            <Lock size={14} />
            {sv ? 'EXKLUSIVT FÖR ELEVER' : 'EXCLUSIVE FOR STUDENTS'}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-3 leading-tight" style={{ color: 'var(--text-primary)' }}>
            Tajweed Guide
            <br />
            <span className="text-gold">{sv ? 'Exklusiv digital lärobok' : 'Exclusive digital textbook'}</span>
          </h2>
          <div className="mt-6 p-5 rounded-2xl max-w-2xl mx-auto" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid rgba(206,150,8,0.15)' }}>
            <p className="text-sm italic" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              {sv
                ? '\u201DDe bästa bland er är de som lär sig Koranen och lär ut den.\u201D'
                : '\u201DThe best among you are those who learn the Quran and teach it.\u201D'}
            </p>
            <p className="text-xs mt-2" style={{ color: 'var(--text-muted)' }}>
              {sv ? '\u2014 Profeten Muhammad \uFDFA (\u1E62a\u1E25\u012B\u1E25 al-Bukh\u0101r\u012B)' : '\u2014 Prophet Muhammad \uFDFA (\u1E62a\u1E25\u012B\u1E25 al-Bukh\u0101r\u012B)'}
            </p>
          </div>
        </div>

        {/* Introduction */}
        <div className="max-w-4xl mx-auto mb-16">
          <h3 className="text-xl font-bold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>
            {sv ? 'En omfattande digital lärobok i tajweed' : 'A comprehensive digital tajweed textbook'}
          </h3>
          <p className="text-base text-center leading-relaxed mb-4" style={{ color: 'var(--text-secondary)' }}>
            {sv
              ? 'Vår Tajweed Guide är en noggrant utformad och strukturerad digital lärobok som guidar eleven från grunderna till avancerad nivå i tajweed.'
              : 'Our Tajweed Guide is a carefully designed and structured digital textbook that guides the student from basics to advanced level in tajweed.'}
          </p>
          <p className="text-sm text-center" style={{ color: 'var(--text-muted)' }}>
            {sv
              ? 'Boken är utvecklad med ett pedagogiskt upplägg, tydliga exempel från Koranen i Uthmani-skrift, samt interaktiva övningar som stärker både förståelse och tillämpning.'
              : 'The book is developed with a pedagogical structure, clear examples from the Quran in Uthmani script, and interactive exercises that strengthen both understanding and application.'}
          </p>
        </div>

        {/* Exclusive access */}
        <div className="p-6 rounded-2xl mb-16 max-w-4xl mx-auto" style={{ background: 'linear-gradient(135deg, rgba(206,150,8,0.06) 0%, rgba(206,150,8,0.02) 100%)', border: '2px solid rgba(206,150,8,0.2)' }}>
          <div className="flex items-center gap-3 mb-4">
            <Lock size={22} className="text-gold" />
            <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
              {sv ? 'Exklusiv åtkomst' : 'Exclusive access'}
            </h3>
          </div>
          <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
            {sv ? 'Denna bok är endast tillgänglig via vår plattform:' : 'This book is only available via our platform:'}
          </p>
          <div className="space-y-2">
            {(sv
              ? ['Endast för registrerade elever', 'Åtkomst kräver personlig kod från lärare', 'Upphovsrättsskyddad \u2014 kan inte kopieras eller laddas ner']
              : ['Only for registered students', 'Access requires personal code from teacher', 'Copyright protected \u2014 cannot be copied or downloaded']
            ).map((item, i) => (
              <div key={i} className="flex items-start gap-3">
                <ShieldCheck size={16} className="text-gold flex-shrink-0 mt-0.5" />
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content features - 5 cards */}
        <div className="mb-16">
          <h3 className="text-xl font-bold mb-8 text-center" style={{ color: 'var(--text-primary)' }}>
            <Star size={20} className="inline-block text-gold mr-2 mb-1" />
            {sv ? 'Vad innehåller Tajweed Guide?' : 'What does Tajweed Guide contain?'}
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Structured content */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }} data-testid="tajweed-feature-0">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(206,150,8,0.1)', border: '1px solid rgba(206,150,8,0.15)' }}>
                <BookOpen size={20} className="text-gold" />
              </div>
              <h4 className="text-base font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {sv ? 'Strukturerat innehåll' : 'Structured content'}
              </h4>
              <ul className="space-y-1">
                <li className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? '26 kapitel \u2014 från grunder till avancerad tajweed' : '26 chapters \u2014 from basics to advanced tajweed'}</li>
                <li className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Tydliga förklaringar och steg-för-steg uppbyggnad' : 'Clear explanations and step-by-step structure'}</li>
              </ul>
            </div>

            {/* Three languages */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }} data-testid="tajweed-feature-1">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(206,150,8,0.1)', border: '1px solid rgba(206,150,8,0.15)' }}>
                <Languages size={20} className="text-gold" />
              </div>
              <h4 className="text-base font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {sv ? 'Tre språk' : 'Three languages'}
              </h4>
              <p className="text-sm mb-2" style={{ color: 'var(--text-muted)' }}>{sv ? 'Fullständigt innehåll på:' : 'Full content in:'}</p>
              <ul className="space-y-1">
                {(sv ? ['Svenska', 'Engelska', 'Arabiska'] : ['Swedish', 'English', 'Arabic']).map((lang, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--gold)' }}></span>{lang}
                  </li>
                ))}
              </ul>
            </div>

            {/* Interactive exercises */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }} data-testid="tajweed-feature-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(206,150,8,0.1)', border: '1px solid rgba(206,150,8,0.15)' }}>
                <GraduationCap size={20} className="text-gold" />
              </div>
              <h4 className="text-base font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {sv ? 'Interaktiva övningar' : 'Interactive exercises'}
              </h4>
              <ul className="space-y-1">
                <li className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? '80+ quizfrågor' : '80+ quiz questions'}</li>
                <li className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Direkt feedback' : 'Instant feedback'}</li>
                <li className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Poängsystem för progression' : 'Score system for progression'}</li>
              </ul>
            </div>

            {/* Authentic examples */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }} data-testid="tajweed-feature-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(206,150,8,0.1)', border: '1px solid rgba(206,150,8,0.15)' }}>
                <BookMarked size={20} className="text-gold" />
              </div>
              <h4 className="text-base font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {sv ? 'Autentiska exempel' : 'Authentic examples'}
              </h4>
              <ul className="space-y-1">
                <li className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Koranverser i Uthmani-skrift' : 'Quran verses in Uthmani script'}</li>
                <li className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Färgkodade tajweed-regler' : 'Color-coded tajweed rules'}</li>
                <li className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Praktiska tillämpningar' : 'Practical applications'}</li>
              </ul>
            </div>

            {/* Three levels */}
            <div className="p-6 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }} data-testid="tajweed-feature-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(206,150,8,0.1)', border: '1px solid rgba(206,150,8,0.15)' }}>
                <Layers size={20} className="text-gold" />
              </div>
              <h4 className="text-base font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                {sv ? 'Fyra nivåer' : 'Four levels'}
              </h4>
              <ul className="space-y-1">
                <li className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Nivå 1 — Grunder' : 'Level 1 — Basics'}</li>
                <li className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Nivå 2 — Uttal' : 'Level 2 — Pronunciation'}</li>
                <li className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Nivå 3 — Regler' : 'Level 3 — Rules'}</li>
                <li className="text-sm" style={{ color: 'var(--text-secondary)' }}>{sv ? 'Nivå 4 — Avancerat' : 'Level 4 — Advanced'}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Quran verse */}
        <div className="p-6 sm:p-8 rounded-2xl text-center mb-16 max-w-3xl mx-auto" style={{ backgroundColor: 'var(--bg-secondary)', border: '2px solid rgba(206,150,8,0.2)' }}>
          <p className="text-sm font-bold mb-3 uppercase tracking-wider" style={{ color: 'var(--gold)' }}>
            {sv ? 'Allah säger' : 'Allah says'}
          </p>
          <p style={{ fontFamily: "'Scheherazade New', 'Amiri', serif", fontSize: '1.8rem', color: 'var(--gold)', direction: 'rtl', lineHeight: 2.2 }}>
            وَرَتِّلِ ٱلْقُرْءَانَ تَرْتِيلًا
          </p>
          <p className="text-sm mt-3 italic" style={{ color: 'var(--text-secondary)' }}>
            {sv ? '\u201D\u2026och recitera Koranen med tydlig recitation.\u201D' : '\u201D\u2026and recite the Quran with measured recitation.\u201D'}
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{'\u2014'} Quran (73:4)</p>
        </div>

        {/* CTA button */}
        <div className="text-center mb-20">
          <Link to="/tajweed-bok" className="btn-gold inline-flex items-center gap-2 text-base" data-testid="tajweed-guide-cta">
            {sv ? 'Logga in med din kod' : 'Login with your code'} <ArrowRight size={18} />
          </Link>
        </div>

        {/* ─── Viktig påminnelse ─── */}
        <div className="max-w-4xl mx-auto mb-16 space-y-8">

          {/* Viktig påminnelse */}
          <div className="p-6 sm:p-8 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(206,150,8,0.06) 0%, rgba(206,150,8,0.02) 100%)', border: '2px solid rgba(206,150,8,0.2)' }}>
            <div className="flex items-center gap-3 mb-4">
              <Headphones size={22} className="text-gold" />
              <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {sv ? 'Viktig påminnelse om Tajweed' : 'Important reminder about Tajweed'}
              </h3>
            </div>
            <p className="text-base font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
              {sv ? 'Tajweed är inte enbart teori som kan läras från en bok.' : 'Tajweed is not merely theory that can be learned from a book.'}
            </p>
            <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              {sv ? 'Att läsa och förstå reglerna är viktigt, men det räcker inte i sig. För att verkligen bemästra tajweed behöver man lyssna, härma och bli korrigerad av en lärare.'
                   : 'Reading and understanding the rules is important, but it is not enough on its own. To truly master tajweed, one needs to listen, imitate and be corrected by a teacher.'}
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              {sv ? 'Tajweed handlar om ljud, uttal och precision \u2014 och detta lärs bäst genom direkt undervisning.'
                   : 'Tajweed is about sound, pronunciation and precision \u2014 and this is best learned through direct instruction.'}
            </p>
          </div>

          {/* Teori + praktik */}
          <div className="p-6 sm:p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              {sv ? 'Teori + praktik = full förståelse' : 'Theory + practice = full understanding'}
            </h3>
            <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              {sv ? 'Vår Tajweed Guide ger dig en stark teoretisk grund, men den är utformad för att användas tillsammans med undervisning. Genom att kombinera:'
                   : 'Our Tajweed Guide gives you a strong theoretical foundation, but it is designed to be used together with teaching. By combining:'}
            </p>
            <div className="space-y-3 mb-5">
              {(sv ? [
                { icon: BookMarked, text: 'Teori (regler och förklaringar)' },
                { icon: Headphones, text: 'Lyssning (korrekt recitation)' },
                { icon: GraduationCap, text: 'Vägledning (lärare som rättar dig)' },
              ] : [
                { icon: BookMarked, text: 'Theory (rules and explanations)' },
                { icon: Headphones, text: 'Listening (correct recitation)' },
                { icon: GraduationCap, text: 'Guidance (teacher who corrects you)' },
              ]).map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}>
                    <Icon size={18} className="text-gold flex-shrink-0" />
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{item.text}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-sm italic" style={{ color: 'var(--text-secondary)' }}>
              {sv ? 'kan du uppnå en korrekt och vacker recitation av Koranen.' : 'you can achieve a correct and beautiful recitation of the Quran.'}
            </p>
          </div>

          {/* Vår metod + Viktigt */}
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 sm:p-8 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(11,61,46,0.06) 0%, rgba(11,61,46,0.02) 100%)', border: '2px solid rgba(11,61,46,0.15)' }}>
              <div className="flex items-center gap-3 mb-4">
                <Sparkles size={20} style={{ color: '#0B3D2E' }} />
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? 'Vår metod' : 'Our method'}
                </h3>
              </div>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                {sv ? 'Vi uppmuntrar alla elever att:' : 'We encourage all students to:'}
              </p>
              <div className="space-y-2">
                {(sv
                  ? ['Studera tajweed', 'Lyssna noggrant', 'Läsa för en lärare', 'Få kontinuerlig korrigering']
                  : ['Study tajweed', 'Listen carefully', 'Read to a teacher', 'Get continuous correction']
                ).map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Sparkles size={14} className="text-gold flex-shrink-0 mt-0.5" />
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 sm:p-8 rounded-2xl" style={{ backgroundColor: 'rgba(234,179,8,0.04)', border: '2px solid rgba(234,179,8,0.2)' }}>
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle size={20} className="text-gold" />
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? 'Viktigt' : 'Important'}
                </h3>
              </div>
              <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                {sv ? 'Tajweed är en vetenskap som traditionellt överförs från lärare till elev (talaqqī).'
                     : 'Tajweed is a science traditionally transmitted from teacher to student (talaqqī).'}
              </p>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)', lineHeight: 1.8 }}>
                {sv ? 'Därför rekommenderas det starkt att inte enbart förlita sig på självstudier.'
                     : 'Therefore, it is strongly recommended not to rely solely on self-study.'}
              </p>
            </div>
          </div>

        </div>

        {/* ─── Lower sections ─── */}
        <div className="max-w-4xl mx-auto space-y-8">

          {/* Om denna bok */}
          <div className="p-6 sm:p-8 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(206,150,8,0.08) 0%, rgba(206,150,8,0.02) 100%)', border: '2px solid rgba(206,150,8,0.25)' }}>
            <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--gold)' }}>
              {sv ? 'Om denna bok' : 'About This Book'}
            </h3>
            <p className="leading-relaxed" style={{ color: 'var(--text-secondary)', lineHeight: 1.9 }}>
              {sv ? 'Denna Tajweed Guide är en översättning och pedagogisk sammanställning av klassisk tajweed-vetenskap.'
                   : 'This Tajweed Guide is a translation and pedagogical compilation of classical tajweed science.'}
            </p>
            <p className="mt-3" style={{ color: 'var(--text-secondary)', lineHeight: 1.9 }}>
              {sv ? 'Den är inte skriven från grunden, utan bygger på erkända verk från pålitliga lärda.'
                   : 'It is not written from scratch, but builds on recognized works from reliable scholars.'}
            </p>
            <p className="mt-3 text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
              {sv ? 'Huvudreferens:' : 'Main reference:'}
            </p>
            <div className="mt-4 p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <p style={{ fontFamily: "'Scheherazade New', 'Amiri', serif", fontSize: '2rem', color: 'var(--gold)', direction: 'rtl', textAlign: 'center', lineHeight: 2 }}>
                التَّجْوِيدُ المُصَوَّر
              </p>
              <p className="text-center font-bold mt-2" style={{ color: 'var(--text-primary)' }}>
                {sv ? 'av Ayman Rushdi Suweid' : 'by Ayman Rushdi Suweid'}
              </p>
              <p className="text-center mt-1" style={{ fontFamily: "'Scheherazade New', 'Amiri', serif", fontSize: '1.3rem', color: 'var(--text-muted)', direction: 'rtl' }}>
                د. أيمن رشدي سُوَيْد
              </p>
              <p className="text-center text-sm mt-3" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                {sv ? "Dr. Ayman Suweid har under mer än 40 år samlat och systematiserat kunskap inom \u02BFilm al-tajw\u012Bd (\u0639\u0644\u0645 \u0627\u0644\u062A\u062C\u0648\u064A\u062F) från lärda världen över. Hans arbete anses vara en av de mest omfattande moderna referenserna inom tajweed."
                     : "Dr. Ayman Suweid has spent more than 40 years collecting and systematizing knowledge of \u02BFilm al-tajw\u012Bd (\u0639\u0644\u0645 \u0627\u0644\u062A\u062C\u0648\u064A\u062F) from scholars around the world. His work is considered one of the most comprehensive modern references in tajweed."}
              </p>
            </div>
          </div>

          {/* Vikten av Tajweed + Vad är Tajweed */}
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 sm:p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3 mb-4">
                <Scale size={22} style={{ color: '#0B3D2E' }} />
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? 'Vikten av Tajweed' : 'The Importance of Tajweed'}
                </h3>
              </div>
              <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
                {sv ? 'Att läsa Koranen korrekt är en skyldighet:' : 'Reading the Quran correctly is an obligation:'}
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <ShieldCheck size={16} className="text-gold flex-shrink-0 mt-0.5" />
                  <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                    {sv ? <>Att undvika tydliga uttalsfel (<span style={{ fontFamily: "'Scheherazade New', serif" }}>لحن جلي</span>) är <strong>obligatoriskt</strong></>
                         : <>Avoiding clear pronunciation errors (<span style={{ fontFamily: "'Scheherazade New', serif" }}>لحن جلي</span>) is <strong>obligatory</strong></>}
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <ShieldCheck size={16} className="text-gold flex-shrink-0 mt-0.5" />
                  <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                    {sv ? <>Att fördjupa sig i tajweed är en <strong>kollektiv skyldighet</strong> (<span style={{ fontFamily: "'Scheherazade New', serif" }}>فرض كفاية</span>)</>
                         : <>Studying tajweed in depth is a <strong>collective obligation</strong> (<span style={{ fontFamily: "'Scheherazade New', serif" }}>فرض كفاية</span>)</>}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-3 mb-4">
                <Brain size={22} style={{ color: '#0B3D2E' }} />
                <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                  {sv ? 'Vad är Tajweed?' : 'What is Tajweed?'}
                </h3>
              </div>
              <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>
                {sv ? 'Tajweed är vetenskapen om att:' : 'Tajweed is the science of:'}
              </p>
              <div className="space-y-2">
                {(sv ? [
                  { text: 'Ge varje bokstav dess rättigheter', ar: 'حقوق الحروف' },
                  { text: 'Ge varje bokstav dess egenskaper', ar: 'صفات الحروف' },
                  { text: 'Uttala från rätt artikulationspunkt', ar: 'مخرج' },
                ] : [
                  { text: 'Give every letter its rights', ar: 'حقوق الحروف' },
                  { text: 'Give every letter its attributes', ar: 'صفات الحروف' },
                  { text: 'Pronounce from the correct point', ar: 'مخرج' },
                ]).map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Sparkles size={16} className="text-gold flex-shrink-0 mt-0.5" />
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {item.text} (<span style={{ fontFamily: "'Scheherazade New', serif", color: 'var(--gold)' }}>{item.ar}</span>)
                    </p>
                  </div>
                ))}
              </div>
              <p className="text-xs mt-4 italic" style={{ color: 'var(--text-muted)' }}>
                {sv ? 'utan överdrift eller brist.' : 'without exaggeration or deficiency.'}
              </p>
            </div>
          </div>

          {/* Hadith */}
          <div className="p-6 sm:p-8 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.06) 0%, rgba(34,197,94,0.01) 100%)', border: '2px solid rgba(34,197,94,0.2)' }}>
            <div className="flex items-center gap-3 mb-4">
              <Sparkles size={20} style={{ color: '#22c55e' }} />
              <p className="text-sm font-bold uppercase tracking-wider" style={{ color: '#22c55e' }}>Hadith</p>
            </div>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
              {sv ? <><strong>Aisha bint Abi Bakr</strong> (رضي الله عنها) berättade att Profeten ﷺ sade:</>
                   : <><strong>Aisha bint Abi Bakr</strong> (رضي الله عنها) narrated that the Prophet ﷺ said:</>}
            </p>
            <div className="p-5 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <p style={{ fontFamily: "'Scheherazade New', 'Amiri', serif", fontSize: '1.4rem', color: 'var(--text-primary)', direction: 'rtl', textAlign: 'center', lineHeight: 2.2 }}>
                الْمَاهِرُ بِالْقُرْآنِ مَعَ السَّفَرَةِ الْكِرَامِ الْبَرَرَةِ
              </p>
              <p style={{ fontFamily: "'Scheherazade New', 'Amiri', serif", fontSize: '1.4rem', color: 'var(--text-primary)', direction: 'rtl', textAlign: 'center', lineHeight: 2.2 }}>
                وَالَّذِي يَقْرَأُ الْقُرْآنَ وَيَتَتَعْتَعُ فِيهِ وَهُوَ عَلَيْهِ شَاقٌّ لَهُ أَجْرَانِ
              </p>
            </div>
            <div className="mt-5 space-y-3">
              <div className="flex gap-3">
                <span className="w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#22c55e' }}></span>
                <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                  {sv ? <><strong style={{ color: 'var(--text-primary)' }}>Den som är skicklig i Koranen</strong> är med de ädla änglarna.</>
                       : <><strong style={{ color: 'var(--text-primary)' }}>The one who is skilled in the Quran</strong> is with the noble angels.</>}
                </p>
              </div>
              <div className="flex gap-3">
                <span className="w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#22c55e' }}></span>
                <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                  {sv ? <><strong style={{ color: 'var(--text-primary)' }}>Och den som läser och finner det svårt</strong> får <strong>dubbel belöning</strong>.</>
                       : <><strong style={{ color: 'var(--text-primary)' }}>And the one who reads and finds it difficult</strong> receives <strong>double reward</strong>.</>}
                </p>
              </div>
            </div>
            <p className="text-xs mt-4 text-right" style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
              {'\u2014'} Ṣaḥīḥ al-Bukhārī & Ṣaḥīḥ Muslim
            </p>
          </div>

        </div>
      </div>
    </section>
  );
};

export default TajweedGuideSection;
